import { useLayoutEffect } from "react";
import { createBrowserRouter } from "react-router";
import { RouteErrorFallback } from "@/components/ErrorBoundary";
import InsightDetailPage from "@/pages/InsightDetailPage";
import InsightsListPage from "@/pages/InsightsListPage";

/**
 * News-only client router.
 *
 * The news surface ships a DEDICATED client bundle (news.html → news-entry.tsx)
 * that contains ONLY these three routes — not the full landing SPA. This is the
 * durable fix for the whole class of news bugs: with no `/`, `/about`, `/terms`
 * (etc.) routes in the bundle, the news client can never client-render a
 * landing page it has no data for (the root cause of the stale-data and
 * "issue not found" symptoms).
 *
 * Any other path — a navbar/footer link to `/`, `/about`, `/terms`, … — hits
 * the catch-all, which does a full-document load to that real path so the SWA
 * (or the news-renderer) serves it fresh.
 */

/** Catch-all: full-document navigate to whatever path was requested. */
function HardRedirectToPath() {
  useLayoutEffect(() => {
    const { pathname, search, hash } = window.location;
    window.location.replace(`${pathname}${search}${hash}`);
  }, []);
  return null;
}

export const newsRouter = createBrowserRouter([
  {
    path: "/news",
    Component: InsightsListPage,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/news/:date",
    Component: InsightDetailPage,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/news/:date/:slug",
    Component: InsightDetailPage,
    errorElement: <RouteErrorFallback />,
  },
  { path: "*", Component: HardRedirectToPath },
]);

// ---------------------------------------------------------------------------
// Scroll-to-top on new navigations (mirrors app/routes.tsx). Most in-news
// links are `reloadDocument` full loads, but the news list's "show more" and
// any residual client transitions still benefit from this.
// ---------------------------------------------------------------------------
let previousPathname = window.location.pathname;

newsRouter.subscribe((state) => {
  if (state.navigation.state !== "idle") return;
  if (state.historyAction !== "PUSH") return;
  const nextPathname = state.location.pathname;
  if (nextPathname === previousPathname) return;
  previousPathname = nextPathname;
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
});
